import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Space,
  Input,
  Tag,
  Modal,
  Form,
  DatePicker,
  Select,
  message,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Alert,
  Divider,
  Typography,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
  LinkOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import { ContainerWithInfo } from "../../pages/ContainersAdmin";
import { supabase } from "../../lib/supabase";
import { getContainersByMasterContainer } from "../../services/masterContainerService";
import {
  downloadAdminContainersCsv,
  formatStorageShare,
  type ContainerListStats,
} from "../../utils/adminContainersExport";

const { Option } = Select;
const { Title, Text } = Typography;

// Define styled components
const CompactTable = styled.div`
  .compact-table {
    .ant-table {
      font-size: 13px;
    }

    .ant-table-thead > tr > th {
      padding: 8px 16px;
    }

    .ant-table-tbody > tr > td {
      padding: 4px 16px;
    }

    .ant-table-cell {
      white-space: nowrap;
    }

    .ant-tag {
      margin-right: 0;
      font-size: 11px;
      padding: 0 4px;
      line-height: 18px;
    }

    .ant-btn {
      font-size: 12px;
      padding: 0 8px;
      height: 24px;
    }

    .ant-space {
      gap: 4px !important;
    }
  }
`;

interface ContainersListProps {
  containers: ContainerWithInfo[];
  loading: boolean;
  onRefresh: () => void;
  onViewDetails?: (containerId: number) => void;
  defaultSort?: string;
}

const ContainersList: React.FC<ContainersListProps> = ({
  containers,
  loading,
  onRefresh,
  onViewDetails,
  defaultSort,
}) => {
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingContainer, setEditingContainer] =
    useState<ContainerWithInfo | null>(null);
  const [transportTypes, setTransportTypes] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [originPorts, setOriginPorts] = useState<
    Array<{ id: number; name: string; country: string }>
  >([]);
  const [destinationPorts, setDestinationPorts] = useState<
    Array<{ id: number; name: string; country: string }>
  >([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [transportTypeFilter, setTransportTypeFilter] = useState<number | null>(
    null
  );
  const [masterContainerFilter, setMasterContainerFilter] = useState<
    number | null
  >(null);
  const [masterContainers, setMasterContainers] = useState<
    Array<{
      id: number;
      reference: string | null;
    }>
  >([]);
  const [containersInMasterContainer, setContainersInMasterContainer] =
    useState<number[]>([]);
  const [commonTransportTypes, setCommonTransportTypes] = useState<{
    seafreight?: number;
    airfreight?: number;
  }>({});
  const [containerStats, setContainerStats] = useState<
    Record<number, ContainerListStats>
  >({});
  const [statsLoading, setStatsLoading] = useState(false);
  const [containerMasterMap, setContainerMasterMap] = useState<
    Record<number, { id: number; reference: string | null }>
  >({});

  // Bulk status update state
  const [selectedContainerIds, setSelectedContainerIds] = useState<number[]>(
    []
  );
  const [isBulkStatusModalVisible, setIsBulkStatusModalVisible] =
    useState(false);
  const [selectedBulkStatus, setSelectedBulkStatus] = useState<string>("");
  const [isBulkStatusUpdating, setIsBulkStatusUpdating] = useState(false);
  const [bulkUpdatePreview, setBulkUpdatePreview] = useState<{
    containers: ContainerWithInfo[];
    bookings: Array<{
      id: number;
      reference: string;
      company_name: string;
      current_status: string;
      new_status: string;
      container_id: number;
    }>;
  }>({ containers: [], bookings: [] });
  const [previewLoading, setPreviewLoading] = useState(false);

  // Available container statuses
  const containerStatuses = [
    { value: "planned", label: "Planned" },
    { value: "departed", label: "Departed" },
    { value: "arrived", label: "Arrived" },
    { value: "customs", label: "Customs" },
    { value: "completed", label: "Completed" },
  ];

  // Status colors for booking statuses
  const statusColors: Record<string, string> = {
    pending: "default",
    approved: "green",
    in_transit: "blue",
    customs_pending_clearance: "orange",
    customs_cleared: "green",
    fyco: "blue",
    completed: "green",
    cancelled: "red",
  };

  // Function to normalize booking status for display
  const normalizeBookingStatus = (status: string): string => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Function to map container status to booking status
  const mapContainerStatusToBookingStatus = (
    containerStatus: string
  ): string => {
    if (containerStatus === "customs") {
      return "customs_pending_clearance";
    } else if (["planned", "departed", "arrived"].includes(containerStatus)) {
      return "in_transit";
    } else {
      return containerStatus; // For completed or other statuses
    }
  };

  // Handle row selection change
  const rowSelection = {
    type: "checkbox" as const,
    selectedRowKeys: selectedContainerIds,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedContainerIds(selectedRowKeys as number[]);
    },
  };

  // Function to show bulk status update modal
  const showBulkStatusUpdateModal = async () => {
    if (selectedContainerIds.length === 0) {
      message.error("Please select at least one container");
      return;
    }

    setIsBulkStatusModalVisible(true);
    setPreviewLoading(true);

    try {
      // Get selected containers
      const selectedContainers = containers.filter((container) =>
        selectedContainerIds.includes(container.id)
      );

      // Fetch bookings for selected containers
      const { data: bookingsData, error: bookingsError } = await supabase
        .from("bookings")
        .select(
          `
          id,
          reference,
          status,
          container_id,
          company_id,
          company:company_id(id, name)
        `
        )
        .in("container_id", selectedContainerIds);

      if (bookingsError) throw bookingsError;

      // Prepare preview data
      const previewBookings =
        bookingsData?.map((booking) => ({
          id: booking.id,
          reference: booking.reference || `EFF${booking.id}`,
          company_name: booking.company?.name || "-",
          current_status: booking.status || "pending",
          new_status: mapContainerStatusToBookingStatus(
            selectedBulkStatus || "planned"
          ),
          container_id: booking.container_id,
        })) || [];

      setBulkUpdatePreview({
        containers: selectedContainers,
        bookings: previewBookings,
      });
    } catch (error) {
      console.error("Error loading bulk update preview:", error);
      message.error("Failed to load update preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Function to update bulk container status
  const updateBulkContainerStatus = async () => {
    if (selectedContainerIds.length === 0 || !selectedBulkStatus) return;

    try {
      setIsBulkStatusUpdating(true);

      // 1. Update container statuses
      const { error: containerError } = await supabase
        .from("containers")
        .update({ status: selectedBulkStatus })
        .in("id", selectedContainerIds);

      if (containerError) throw containerError;

      // 2. Map container status to appropriate booking status
      const bookingStatus =
        mapContainerStatusToBookingStatus(selectedBulkStatus);

      // 3. Update all bookings in selected containers
      const { error: bookingsError } = await supabase
        .from("bookings")
        .update({ status: bookingStatus })
        .in("container_id", selectedContainerIds);

      if (bookingsError) throw bookingsError;

      // Success
      message.success(
        `Updated status for ${selectedContainerIds.length} containers and their bookings`
      );
      setIsBulkStatusModalVisible(false);
      setSelectedContainerIds([]);
      setSelectedBulkStatus("");

      // Refresh data
      onRefresh();
    } catch (error) {
      console.error("Error updating bulk container status:", error);
      message.error("Failed to update container statuses");
    } finally {
      setIsBulkStatusUpdating(false);
    }
  };

  // Handle container status change to update preview
  const handleBulkStatusChange = async (status: string) => {
    setSelectedBulkStatus(status);

    if (status && selectedContainerIds.length > 0) {
      setPreviewLoading(true);

      try {
        // Fetch updated booking data with new status
        const { data: bookingsData, error: bookingsError } = await supabase
          .from("bookings")
          .select(
            `
            id,
            reference,
            status,
            container_id,
            company_id,
            company:company_id(id, name)
          `
          )
          .in("container_id", selectedContainerIds);

        if (bookingsError) throw bookingsError;

        const selectedContainers = containers.filter((container) =>
          selectedContainerIds.includes(container.id)
        );

        const previewBookings =
          bookingsData?.map((booking) => ({
            id: booking.id,
            reference: booking.reference || `EFF${booking.id}`,
            company_name: booking.company?.name || "-",
            current_status: booking.status || "pending",
            new_status: mapContainerStatusToBookingStatus(status),
            container_id: booking.container_id,
          })) || [];

        setBulkUpdatePreview({
          containers: selectedContainers,
          bookings: previewBookings,
        });
      } catch (error) {
        console.error("Error updating preview:", error);
      } finally {
        setPreviewLoading(false);
      }
    }
  };

  // Fetch reference data
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        // Fetch transport types
        const { data: transportData, error: transportError } = await supabase
          .from("booking_transport_types")
          .select("id, name, abbreviation");

        if (transportError) throw transportError;
        setTransportTypes(transportData || []);

        // Identify common transport types by name (case insensitive)
        const commonTypes: {
          seafreight?: number;
          airfreight?: number;
        } = {};

        transportData?.forEach(
          (type: { id: number; name: string; abbreviation?: string }) => {
            const typeName = type.name.toLowerCase();
            if (
              typeName.includes("sea") ||
              typeName.includes("ocean") ||
              typeName === "fcl" ||
              typeName === "lcl"
            ) {
              commonTypes.seafreight = type.id;
            } else if (typeName.includes("air")) {
              commonTypes.airfreight = type.id;
            }
          }
        );

        setCommonTransportTypes(commonTypes);

        // Fetch origin ports
        const { data: originData, error: originError } = await supabase
          .from("booking_origin_port")
          .select("id, name, country");

        if (originError) throw originError;
        setOriginPorts(originData || []);

        // Fetch destination ports
        const { data: destData, error: destError } = await supabase
          .from("booking_destination_port")
          .select("id, name, country");

        if (destError) throw destError;
        setDestinationPorts(destData || []);

        // Fetch master containers
        const { data: masterData, error: masterError } = await supabase
          .from("master_containers")
          .select("id, reference")
          .order("reference");

        if (masterError) throw masterError;
        setMasterContainers(masterData || []);
      } catch (error) {
        console.error("Error fetching reference data:", error);
        message.error("Failed to load reference data");
      }
    };

    fetchReferenceData();
  }, []);

  // Fetch stats for containers
  useEffect(() => {
    const fetchContainerStats = async () => {
      if (!containers || containers.length === 0) return;

      setStatsLoading(true);

      try {
        // Create a stats object to store calculations
        const stats: Record<number, ContainerListStats> = {};

        // Initialize stats for all containers
        containers.forEach((container) => {
          stats[container.id] = {
            totalCBM: 0,
            storageCBM: 0,
            totalWeight: 0,
            bookingsCount: 0,
            uniqueProducts: 0,
          };
        });

        // Fetch all bookings with products for these containers
        const containerIds = containers.map((c) => c.id);
        const data: Array<{
          id: number;
          container_id: number;
          booking_delivery: Array<{ id: number }> | null;
          product_info: Array<{
            id: number;
            name: string;
            gross_weight: number;
            cbm: number;
          }>;
        }> = [];
        const batchSize = 1000;
        let offset = 0;
        while (true) {
          const { data: batch, error } = await supabase
            .from("bookings")
            .select(
              `
            id,
            container_id,
            booking_delivery(id),
            product_info:booking_product(
              id,
              name,
              gross_weight,
              cbm
            )
          `,
            )
            .in("container_id", containerIds)
            .range(offset, offset + batchSize - 1);

          if (error) throw error;
          const rows = batch || [];
          data.push(...rows);
          if (rows.length < batchSize) break;
          offset += batchSize;
        }

        if (data) {
          // Process each booking
          data.forEach((booking) => {
              const containerId = booking.container_id;
              if (!containerId || !stats[containerId]) return;

              // Count this booking
              stats[containerId].bookingsCount++;

              const goesToStorage =
                !booking.booking_delivery ||
                booking.booking_delivery.length === 0;
              let bookingCbm = 0;

              // Process products for this booking
              if (booking.product_info && Array.isArray(booking.product_info)) {
                const uniqueProductIds = new Set<number>();

                booking.product_info.forEach((product) => {
                    // Add to unique products set
                    uniqueProductIds.add(product.id);

                    const cbm = Number(product.cbm) || 0;
                    bookingCbm += cbm;
                    stats[containerId].totalCBM += cbm;
                    stats[containerId].totalWeight +=
                      Number(product.gross_weight) || 0;
                  });

                // Add unique products count
                stats[containerId].uniqueProducts += uniqueProductIds.size;
              }

              if (goesToStorage) {
                stats[containerId].storageCBM += bookingCbm;
              }
            });
        }

        setContainerStats(stats);
      } catch (error) {
        console.error("Error fetching container stats:", error);
        message.error("Failed to load container statistics");
      } finally {
        setStatsLoading(false);
      }
    };

    const fetchContainerMasterMappings = async () => {
      if (!containers || containers.length === 0) return;

      try {
        const containerIds = containers.map((c) => c.id);
        const { data, error } = await supabase
          .from("master_containers_containers")
          .select(
            `
            container_id,
            master_container:master_containers(id, reference)
          `
          )
          .in("container_id", containerIds);

        if (error) throw error;

        const mappings: Record<
          number,
          { id: number; reference: string | null }
        > = {};

        if (data) {
          data.forEach(
            (item: {
              container_id: number;
              master_container: Array<{ id: number; reference: string | null }>;
            }) => {
              if (item.master_container && item.master_container.length > 0) {
                mappings[item.container_id] = item.master_container[0];
              }
            }
          );
        }

        setContainerMasterMap(mappings);
      } catch (error) {
        console.error("Error fetching container master mappings:", error);
      }
    };

    fetchContainerStats();
    fetchContainerMasterMappings();
  }, [containers]);

  // Fetch containers in selected master container when filter changes
  useEffect(() => {
    const fetchContainersInMaster = async () => {
      if (!masterContainerFilter) {
        setContainersInMasterContainer([]);
        return;
      }

      try {
        const result = await getContainersByMasterContainer(
          masterContainerFilter
        );
        if (result.error) {
          console.error("Error fetching containers in master:", result.error);
          setContainersInMasterContainer([]);
        } else {
          setContainersInMasterContainer(result.data);
        }
      } catch (error) {
        console.error("Error fetching containers in master:", error);
        setContainersInMasterContainer([]);
      }
    };

    fetchContainersInMaster();
  }, [masterContainerFilter]);

  const getContainerStatusColor = (status: string) => {
    const statusMap: Record<string, string> = {
      planned: "default",
      departed: "processing",
      arrived: "success",
      customs: "warning",
      delivered: "success",
      completed: "success",
      cancelled: "error",
    };
    return statusMap[status?.toLowerCase()] || "default";
  };

  // Filter containers based on search text, transport type, and master container
  const filteredContainers = containers.filter((container) => {
    const searchLower = searchText.toLowerCase();
    const matchesSearch =
      !searchText ||
      container.reference?.toLowerCase().includes(searchLower) ||
      container.track_and_trace?.toLowerCase().includes(searchLower) ||
      container.status?.toLowerCase().includes(searchLower) ||
      container.origin_port_info?.name.toLowerCase().includes(searchLower) ||
      container.destination_port_info?.name
        .toLowerCase()
        .includes(searchLower) ||
      container.transport_type_info?.name.toLowerCase().includes(searchLower);

    // Apply transport type filter if selected
    const matchesTransportType =
      !transportTypeFilter || container.transport_type === transportTypeFilter;

    // Apply master container filter if selected
    const matchesMasterContainer =
      !masterContainerFilter ||
      containersInMasterContainer.includes(container.id);

    return matchesSearch && matchesTransportType && matchesMasterContainer;
  });

  const showAddModal = () => {
    setEditingContainer(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const showEditModal = (container: ContainerWithInfo) => {
    setEditingContainer(container);
    form.setFieldsValue({
      reference: container.reference,
      transport_type: container.transport_type,
      etd: container.etd ? dayjs(container.etd) : null,
      delayed_etd: container.delayed_etd ? dayjs(container.delayed_etd) : null,
      eta: container.eta ? dayjs(container.eta) : null,
      eta_customer: container.eta_customer
        ? dayjs(container.eta_customer)
        : null,
      track_and_trace: container.track_and_trace,
      origin_port: container.origin_port,
      destination_port: container.destination_port,
    });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleDelete = async (id: number) => {
    try {
      const { error } = await supabase.from("containers").delete().eq("id", id);

      if (error) throw error;
      message.success("Container deleted successfully");
      onRefresh();
    } catch (error) {
      console.error("Error deleting container:", error);
      message.error("Failed to delete container");
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      // Format dates
      const formattedValues = {
        ...values,
        etd: values.etd ? values.etd.format("YYYY-MM-DD") : null,
        delayed_etd: values.delayed_etd
          ? values.delayed_etd.format("YYYY-MM-DD")
          : null,
        eta: values.eta ? values.eta.format("YYYY-MM-DD") : null,
        eta_customer: values.eta_customer
          ? values.eta_customer.format("YYYY-MM-DD")
          : null,
      };

      // For existing containers, preserve the current status value
      // This ensures we don't overwrite the status when editing
      if (editingContainer) {
        // Update existing container - but don't modify status
        const { error } = await supabase
          .from("containers")
          .update(formattedValues)
          .eq("id", editingContainer.id);

        if (error) throw error;
        message.success("Container updated successfully");
      } else {
        // For new containers, set an initial status of "planned"
        const { error } = await supabase
          .from("containers")
          .insert([{ ...formattedValues, status: "planned" }]);

        if (error) throw error;
        message.success("Container added successfully");
      }

      setIsModalVisible(false);
      onRefresh();
    } catch (error) {
      console.error("Error saving container:", error);
      message.error("Failed to save container");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTrackingLinkClick = (
    trackingNumber: string,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    // Open the tracking link exactly as provided, without modifying it
    window.open(trackingNumber, "_blank");
  };

  const columns: ColumnsType<ContainerWithInfo> = [
    {
      title: "Reference",
      dataIndex: "reference",
      key: "reference",
      render: (reference, record) => {
        const transportType = record.transport_type_info?.abbreviation || "";
        return (
          <strong>{reference || `EFF${record.id}${transportType}`}</strong>
        );
      },
    },
    {
      title: "Transport Type",
      dataIndex: ["transport_type_info", "name"],
      key: "transport_type",
      render: (text, record) => record.transport_type_info?.name || "-",
      sorter: (a, b) =>
        (a.transport_type_info?.name || "").localeCompare(
          b.transport_type_info?.name || ""
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={getContainerStatusColor(status)}>
          {status ? status.charAt(0).toUpperCase() + status.slice(1) : "-"}
        </Tag>
      ),
      sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
    },
    {
      title: "Origin",
      dataIndex: ["origin_port_info", "name"],
      key: "origin",
      render: (text, record) =>
        record.origin_port_info ? (
          <Tooltip title={record.origin_port_info.country}>
            {record.origin_port_info.name}
          </Tooltip>
        ) : (
          "-"
        ),
      sorter: (a, b) =>
        (a.origin_port_info?.name || "").localeCompare(
          b.origin_port_info?.name || ""
        ),
    },
    {
      title: "Destination",
      dataIndex: ["destination_port_info", "name"],
      key: "destination",
      render: (text, record) =>
        record.destination_port_info ? (
          <Tooltip title={record.destination_port_info.country}>
            {record.destination_port_info.name}
          </Tooltip>
        ) : (
          "-"
        ),
      sorter: (a, b) =>
        (a.destination_port_info?.name || "").localeCompare(
          b.destination_port_info?.name || ""
        ),
    },
    {
      title: "ETD",
      dataIndex: "etd",
      key: "etd",
      render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
      sorter: (a, b) => {
        if (!a.etd && !b.etd) return 0;
        if (!a.etd) return -1;
        if (!b.etd) return 1;
        return dayjs(a.etd).unix() - dayjs(b.etd).unix();
      },
    },
    {
      title: "Delayed ETD",
      dataIndex: "delayed_etd",
      key: "delayed_etd",
      render: (date) =>
        date ? (
          <span>
            {dayjs(date).format("DD/MM/YYYY")}
            <Tag color="orange" style={{ marginLeft: 6 }}>
              Delayed
            </Tag>
          </span>
        ) : (
          "-"
        ),
      sorter: (a, b) => {
        if (!a.delayed_etd && !b.delayed_etd) return 0;
        if (!a.delayed_etd) return -1;
        if (!b.delayed_etd) return 1;
        return dayjs(a.delayed_etd).unix() - dayjs(b.delayed_etd).unix();
      },
    },
    {
      title: "ETA",
      dataIndex: "eta",
      key: "eta",
      render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
      sorter: (a, b) => {
        if (!a.eta && !b.eta) return 0;
        if (!a.eta) return -1;
        if (!b.eta) return 1;
        return dayjs(a.eta).unix() - dayjs(b.eta).unix();
      },
      defaultSortOrder: defaultSort === 'eta' ? 'ascend' : undefined,
    },
    {
      title: "Customer ETA",
      dataIndex: "eta_customer",
      key: "eta_customer",
      render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
      sorter: (a, b) => {
        if (!a.eta_customer && !b.eta_customer) return 0;
        if (!a.eta_customer) return -1;
        if (!b.eta_customer) return 1;
        return dayjs(a.eta_customer).unix() - dayjs(b.eta_customer).unix();
      },
    },
    {
      title: "Days to ETA",
      key: "days_to_eta",
      render: (_, record) => {
        if (!record.eta) return "-";

        const today = dayjs();
        const etaDate = dayjs(record.eta);
        const diff = etaDate.diff(today, "day");

        // Add styling based on urgency
        let color = "";
        if (diff < 0) {
          color = "#ff4d4f"; // Red for past date
        } else if (diff <= 7) {
          color = "#faad14"; // Orange/yellow for within a week
        } else if (diff <= 14) {
          color = "#52c41a"; // Green for within two weeks
        }

        return (
          <span style={{ color: color, fontWeight: color ? "bold" : "normal" }}>
            {diff < 0
              ? `${Math.abs(diff)} days ago`
              : diff === 0
              ? "Today"
              : `${diff} days`}
          </span>
        );
      },
      sorter: (a, b) => {
        if (!a.eta && !b.eta) return 0;
        if (!a.eta) return -1;
        if (!b.eta) return 1;

        const today = dayjs();
        const diffA = dayjs(a.eta).diff(today, "day");
        const diffB = dayjs(b.eta).diff(today, "day");

        return diffA - diffB;
      },
    },
    {
      title: "Bookings",
      key: "bookings_count",
      width: 90,
      render: (_, record) => {
        const stats = containerStats[record.id];
        return statsLoading ? (
          "Loading..."
        ) : stats ? (
          <Badge
            count={stats.bookingsCount}
            showZero
            style={{
              backgroundColor: stats.bookingsCount > 0 ? "#1890ff" : "#999999",
            }}
          />
        ) : (
          "-"
        );
      },
    },
    {
      title: "Products",
      key: "products_count",
      width: 90,
      render: (_, record) => {
        const stats = containerStats[record.id];
        return statsLoading ? (
          "Loading..."
        ) : stats ? (
          <Badge
            count={stats.uniqueProducts}
            showZero
            style={{
              backgroundColor: stats.uniqueProducts > 0 ? "#52c41a" : "#999999",
            }}
          />
        ) : (
          "-"
        );
      },
    },
    {
      title: "Weight",
      key: "total_weight",
      width: 100,
      render: (_, record) => {
        const stats = containerStats[record.id];
        return statsLoading ? (
          "Loading..."
        ) : stats ? (
          <span>{stats.totalWeight.toFixed(2)} kg</span>
        ) : (
          "-"
        );
      },
      sorter: (a, b) => {
        const statsA = containerStats[a.id]?.totalWeight || 0;
        const statsB = containerStats[b.id]?.totalWeight || 0;
        return statsA - statsB;
      },
    },
    {
      title: "CBM",
      key: "total_cbm",
      width: 90,
      render: (_, record) => {
        const stats = containerStats[record.id];
        return statsLoading ? (
          "Loading..."
        ) : stats ? (
          <span>{stats.totalCBM.toFixed(3)} m³</span>
        ) : (
          "-"
        );
      },
      sorter: (a, b) => {
        const statsA = containerStats[a.id]?.totalCBM || 0;
        const statsB = containerStats[b.id]?.totalCBM || 0;
        return statsA - statsB;
      },
    },
    {
      title: "Storage %",
      key: "storage_percent",
      width: 160,
      render: (_, record) => {
        const stats = containerStats[record.id];
        if (statsLoading) return "Loading...";
        const label = formatStorageShare(stats);
        if (label === "-") return "-";
        return (
          <Tooltip title="CBM going into storage (no delivery address) / total CBM in the container">
            <span>{label}</span>
          </Tooltip>
        );
      },
      sorter: (a, b) => {
        const share = (id: number) => {
          const stats = containerStats[id];
          if (!stats || stats.totalCBM <= 0) return -1;
          return stats.storageCBM / stats.totalCBM;
        };
        return share(a.id) - share(b.id);
      },
    },
    {
      title: "Companies",
      key: "companies",
      render: (_, record) => {
        if (!record.booking_list || record.booking_list.length === 0) {
          return <span>0</span>;
        }

        // Get unique company IDs
        const uniqueCompanyIds = new Set<number>();
        record.booking_list.forEach((booking) => {
          if (booking.company_id) {
            uniqueCompanyIds.add(booking.company_id);
          }
        });

        const count = uniqueCompanyIds.size;

        return (
          <Badge
            count={count}
            showZero
            style={{ backgroundColor: count > 0 ? "#722ed1" : "#999999" }}
          />
        );
      },
    },
    {
      title: "Bill of lading (B/L)",
      key: "master_container",
      render: (_, record) => {
        const masterContainer = containerMasterMap[record.id];
        if (!masterContainer) {
          return "-";
        }

        return (
          <Button
            type="link"
            onClick={(e) => {
              e.stopPropagation();
              // Navigate to master containers tab with this master container selected
              window.location.href = `/admin/containers?tab=master-containers&master_id=${masterContainer.id}`;
            }}
            style={{ padding: 0 }}
          >
            {masterContainer.reference || `B/L #${masterContainer.id}`}
          </Button>
        );
      },
    },
    {
      title: "Tracking",
      dataIndex: "track_and_trace",
      key: "track_and_trace",
      render: (text) =>
        text ? (
          <Tooltip title="Open tracking link">
            <Button
              type="link"
              icon={<LinkOutlined />}
              onClick={(e) => handleTrackingLinkClick(text, e)}
              style={{ padding: "0" }}
            />
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit container information (status can only be updated from container details)">
            <Button
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                showEditModal(record);
              }}
              size="small"
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this container?"
            onConfirm={(e) => {
              if (e) e.stopPropagation();
              handleDelete(record.id);
            }}
            okText="Yes"
            cancelText="No"
            onCancel={(e) => {
              if (e) e.stopPropagation();
            }}
          >
            <Button
              icon={<DeleteOutlined />}
              danger
              size="small"
              title="Delete container"
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col flex="auto">
          <Input
            placeholder="Search containers"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<SearchOutlined />}
            allowClear
          />
        </Col>
        <Col>
          <Space>
            <Select
              placeholder="Filter by bill of lading (B/L)"
              allowClear
              showSearch
              optionFilterProp="children"
              style={{ width: 200 }}
              onChange={(value) => setMasterContainerFilter(value)}
              value={masterContainerFilter}
            >
              {masterContainers.map((masterContainer) => (
                <Option key={masterContainer.id} value={masterContainer.id}>
                  {masterContainer.reference ||
                    `B/L #${masterContainer.id}`}
                </Option>
              ))}
            </Select>

            <Button.Group>
              <Button
                type={transportTypeFilter === null ? "primary" : "default"}
                onClick={() => setTransportTypeFilter(null)}
              >
                All Types
              </Button>
              {commonTransportTypes.seafreight && (
                <Button
                  type={
                    transportTypeFilter === commonTransportTypes.seafreight
                      ? "primary"
                      : "default"
                  }
                  onClick={() =>
                    setTransportTypeFilter(
                      commonTransportTypes.seafreight || null
                    )
                  }
                >
                  Sea Freight
                </Button>
              )}
              {commonTransportTypes.airfreight && (
                <Button
                  type={
                    transportTypeFilter === commonTransportTypes.airfreight
                      ? "primary"
                      : "default"
                  }
                  onClick={() =>
                    setTransportTypeFilter(
                      commonTransportTypes.airfreight || null
                    )
                  }
                >
                  Air Freight
                </Button>
              )}
            </Button.Group>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showAddModal}
            >
              Add Container
            </Button>
            <Button
              type="primary"
              icon={<SwapOutlined />}
              onClick={showBulkStatusUpdateModal}
              disabled={selectedContainerIds.length === 0}
              style={{ background: "#faad14", borderColor: "#faad14" }}
            >
              Bulk Update Status ({selectedContainerIds.length})
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => {
                const selected = new Set(selectedContainerIds);
                const rows =
                  selectedContainerIds.length > 0
                    ? containers.filter((container) => selected.has(container.id))
                    : filteredContainers;
                if (rows.length === 0) {
                  message.warning("No containers to export");
                  return;
                }
                const billOfLadingById: Record<number, string> = {};
                rows.forEach((container) => {
                  const master = containerMasterMap[container.id];
                  billOfLadingById[container.id] = master
                    ? master.reference || `B/L #${master.id}`
                    : "";
                });
                downloadAdminContainersCsv(
                  rows,
                  containerStats,
                  billOfLadingById,
                  `containers-${dayjs().format("YYYY-MM-DD")}.csv`,
                );
                message.success(`Exported ${rows.length} containers`);
              }}
              disabled={loading || containers.length === 0}
            >
              {selectedContainerIds.length > 0
                ? `Export selected (${selectedContainerIds.length})`
                : `Export shown (${filteredContainers.length})`}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={onRefresh}>
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      <CompactTable>
        <Table
          columns={columns}
          dataSource={filteredContainers}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 25 }}
          className="compact-table"
          scroll={{ x: 1200 }}
          rowSelection={rowSelection}
          onRow={(record) => ({
            onClick: (event) => {
              // Don't open container details if we clicked on a checkbox
              const target = event.target as HTMLElement;
              const isCheckbox =
                target.tagName === "INPUT" &&
                target.getAttribute("type") === "checkbox";

              if (!isCheckbox && onViewDetails) {
                onViewDetails(record.id);
              }
            },
            style: { cursor: "pointer" },
          })}
          summary={() => {
            // Calculate totals for filtered containers only
            const totalBookings = filteredContainers.reduce(
              (sum, container) => {
                const stats = containerStats[container.id];
                return sum + (stats?.bookingsCount || 0);
              },
              0
            );

            const totalProducts = filteredContainers.reduce(
              (sum, container) => {
                const stats = containerStats[container.id];
                return sum + (stats?.uniqueProducts || 0);
              },
              0
            );

            const totalWeight = filteredContainers.reduce((sum, container) => {
              const stats = containerStats[container.id];
              return sum + (stats?.totalWeight || 0);
            }, 0);

            const totalCBM = filteredContainers.reduce((sum, container) => {
              const stats = containerStats[container.id];
              return sum + (stats?.totalCBM || 0);
            }, 0);

            return (
              <Table.Summary fixed>
                <Table.Summary.Row
                  style={{ fontWeight: "bold", backgroundColor: "#f0f7ff" }}
                >
                  <Table.Summary.Cell index={0} colSpan={10}>
                    <strong>
                      TOTALS ({filteredContainers.length} containers)
                    </strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={10}>
                    <Badge
                      count={totalBookings}
                      overflowCount={9999}
                      style={{
                        backgroundColor: "#1890ff",
                        fontWeight: "bold",
                      }}
                    />
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={11}>
                    <Badge
                      count={totalProducts}
                      overflowCount={9999}
                      style={{
                        backgroundColor: "#52c41a",
                        fontWeight: "bold",
                      }}
                    />
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={12}>
                    {totalWeight.toFixed(2)} kg
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={13}>
                    {totalCBM.toFixed(3)} m³
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={14}>
                    {formatStorageShare({
                      totalCBM,
                      storageCBM: filteredContainers.reduce(
                        (sum, container) =>
                          sum + (containerStats[container.id]?.storageCBM || 0),
                        0,
                      ),
                    })}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell
                    index={15}
                    colSpan={4}
                  ></Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </CompactTable>

      <Modal
        title={editingContainer ? "Edit Container" : "Add Container"}
        open={isModalVisible}
        onCancel={handleCancel}
        onOk={handleSubmit}
        confirmLoading={isSubmitting}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="reference"
                label="Container Reference"
                rules={[{ required: true, message: "Please enter reference" }]}
              >
                <Input placeholder="Enter container reference" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="transport_type"
                label="Transport Type"
                rules={[
                  { required: true, message: "Please select transport type" },
                ]}
              >
                <Select placeholder="Select transport type">
                  {transportTypes.map((type) => (
                    <Option key={type.id} value={type.id}>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Alert
            message="Note: Container Status"
            description="Container status can be updated from the Container Details page or using the Bulk Update Status button above for multiple containers."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="track_and_trace" label="Tracking Number">
                <Input placeholder="Enter tracking number" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="origin_port"
                label="Origin Port"
                rules={[
                  { required: true, message: "Please select origin port" },
                ]}
              >
                <Select
                  placeholder="Select origin port"
                  showSearch
                  optionFilterProp="children"
                >
                  {originPorts.map((port) => (
                    <Option key={port.id} value={port.id}>
                      {port.name} ({port.country})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="destination_port"
                label="Destination Port"
                rules={[
                  { required: true, message: "Please select destination port" },
                ]}
              >
                <Select
                  placeholder="Select destination port"
                  showSearch
                  optionFilterProp="children"
                >
                  {destinationPorts.map((port) => (
                    <Option key={port.id} value={port.id}>
                      {port.name} ({port.country})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="etd"
                label="ETD (original)"
                tooltip="Original booked departure date — used for invoicing"
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="delayed_etd"
                label="Delayed ETD"
                tooltip="Optional operational delayed departure. When set, this is shown instead of the original ETD."
              >
                <DatePicker style={{ width: "100%" }} allowClear />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="eta" label="ETA (Estimated Time of Arrival)">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="eta_customer"
                label="Customer ETA (Expected arrival at customer)"
                tooltip="This is the expected date when goods will arrive at the customer's location after port arrival and customs clearance"
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Bulk Status Update Modal */}
      <Modal
        title="Bulk Update Container Status"
        open={isBulkStatusModalVisible}
        onCancel={() => setIsBulkStatusModalVisible(false)}
        onOk={updateBulkContainerStatus}
        confirmLoading={isBulkStatusUpdating}
        width={1000}
        okText="Update All Containers"
        cancelText="Cancel"
      >
        <Alert
          message="Warning"
          description={`Updating the container status will also update the status of all bookings in ${selectedContainerIds.length} selected containers.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form layout="vertical">
          <Form.Item label="Container Status">
            <Select
              value={selectedBulkStatus}
              onChange={handleBulkStatusChange}
              style={{ width: "100%" }}
              placeholder="Select new status for all containers"
            >
              {containerStatuses.map((status) => (
                <Option key={status.value} value={status.value}>
                  {status.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>

        {selectedBulkStatus && (
          <>
            <Divider />
            <div style={{ marginBottom: 16 }}>
              <Title level={5}>
                Preview: {bulkUpdatePreview.containers.length} Containers and{" "}
                {bulkUpdatePreview.bookings.length} Bookings
              </Title>
              <Text type="secondary">
                The following containers and their bookings will be updated:
              </Text>
            </div>

            {previewLoading ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                Loading preview...
              </div>
            ) : (
              <>
                {/* Containers Preview */}
                <div style={{ marginBottom: 24 }}>
                  <Title level={5}>Containers to Update</Title>
                  <Table
                    size="small"
                    pagination={false}
                    dataSource={bulkUpdatePreview.containers}
                    rowKey="id"
                    columns={[
                      {
                        title: "Container",
                        dataIndex: "reference",
                        key: "reference",
                        render: (reference, record) => (
                          <strong>{reference || `EFF${record.id}`}</strong>
                        ),
                      },
                      {
                        title: "Transport Type",
                        dataIndex: ["transport_type_info", "name"],
                        key: "transport_type",
                        render: (text, record) =>
                          record.transport_type_info?.name || "-",
                      },
                      {
                        title: "Current Status",
                        dataIndex: "status",
                        key: "currentStatus",
                        render: (status) => (
                          <Tag color={getContainerStatusColor(status)}>
                            {status
                              ? status.charAt(0).toUpperCase() + status.slice(1)
                              : "-"}
                          </Tag>
                        ),
                      },
                      {
                        title: "New Status",
                        key: "newStatus",
                        render: () => (
                          <Tag
                            color={getContainerStatusColor(selectedBulkStatus)}
                          >
                            {selectedBulkStatus.charAt(0).toUpperCase() +
                              selectedBulkStatus.slice(1)}
                          </Tag>
                        ),
                      },
                    ]}
                  />
                </div>

                {/* Bookings Preview - Grouped by Container */}
                {bulkUpdatePreview.bookings.length > 0 && (
                  <div>
                    <Title level={5}>
                      Bookings to Update (Grouped by Container)
                    </Title>
                    {bulkUpdatePreview.containers.map((container) => {
                      const containerBookings =
                        bulkUpdatePreview.bookings.filter(
                          (booking) => booking.container_id === container.id
                        );

                      if (containerBookings.length === 0) return null;

                      return (
                        <div key={container.id} style={{ marginBottom: 24 }}>
                          <div
                            style={{
                              backgroundColor: "#f5f5f5",
                              padding: "8px 12px",
                              marginBottom: 8,
                              borderRadius: "4px",
                              fontWeight: "bold",
                            }}
                          >
                            Container:{" "}
                            {container.reference || `EFF${container.id}`}(
                            {containerBookings.length} booking
                            {containerBookings.length !== 1 ? "s" : ""})
                          </div>
                          <Table
                            size="small"
                            pagination={false}
                            dataSource={containerBookings}
                            rowKey="id"
                            columns={[
                              {
                                title: "Booking",
                                dataIndex: "reference",
                                key: "reference",
                                render: (reference) => (
                                  <Tag color="blue">{reference}</Tag>
                                ),
                              },
                              {
                                title: "Company",
                                dataIndex: "company_name",
                                key: "company",
                                render: (text) => text || "-",
                              },
                              {
                                title: "Current Status",
                                dataIndex: "current_status",
                                key: "currentStatus",
                                render: (status) => {
                                  const normalizedStatus =
                                    normalizeBookingStatus(status);
                                  const statusColor =
                                    statusColors[status] || "default";
                                  return (
                                    <Tag color={statusColor}>
                                      {normalizedStatus}
                                    </Tag>
                                  );
                                },
                              },
                              {
                                title: "New Status",
                                dataIndex: "new_status",
                                key: "newStatus",
                                render: (status) => {
                                  const normalizedStatus =
                                    normalizeBookingStatus(status);
                                  const statusColor =
                                    statusColors[status] || "default";
                                  return (
                                    <Tag color={statusColor}>
                                      {normalizedStatus}
                                    </Tag>
                                  );
                                },
                              },
                            ]}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default ContainersList;
